import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Group,
	Menu,
	Skeleton,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import {
	DSAI_HEADER_ROW_STYLES,
	DsAiCell,
	DsAiGridRow,
	dsAiGridRowStyles,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/DataGrid/DataGrid.styles';
import { fadeIn } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.animations';
import { DS_AI_MOTION } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { TableToolbar } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/DataGrid/TableToolbar';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DS_AI_MENU_PROPS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/dropdownMenu.styles';
import { useCoveredMenu } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/useStackedDialogCover';
import type { LibraryPickerState } from '../../useLibraryPicker.types';
import { TablePagination } from '@dealstack/frontend/shared/components/Table/MantineTable/TablePagination';
import { LIBRARY_PAGE_SIZE_OPTIONS } from '../../../ingestion.constants';
import { LibraryRow } from './LibraryRow';
import {
	COUNT_ERROR_ALERT_VARS,
	LIBRARY_COLUMNS,
	NAME_LABEL_STYLES,
	dsAiToolbarButtonStyles,
} from './LibraryPanel.styles';

interface Props {
	library: LibraryPickerState;
	isFullScreen: boolean;
	onToggleFullScreen: () => void;
}

const SKELETON_ROWS = 6;

/** Picks existing documents from the deal's document library. */
export const LibraryPanel = ({ library, isFullScreen, onToggleFullScreen }: Props) => {
	const tCapManage = useTranslationCapManage();
	const filterMenu = useCoveredMenu();
	const pageSizeMenu = useCoveredMenu();
	const { rows } = library;

	return (
		<Stack
			gap={0}
			p="sm"
			styles={{
				root: {
					animation: `${fadeIn} ${DS_AI_MOTION.baseMs}ms ${DS_AI_MOTION.easing} both`,
				},
			}}
		>
			{library.isPageCountError && (
				<Alert color="red" vars={COUNT_ERROR_ALERT_VARS} role="alert">
					{tCapManage('dealstack_ai.ingest.library.count_error')}
					<Button variant="subtle" onClick={library.retryPageCounts}>
						{tCapManage('dealstack_ai.ingest.library.retry_counts')}
					</Button>
				</Alert>
			)}
			<LibraryFrame
				miw={0}
				bd="1px solid gray.4"
				bg="white.0"
				// clip, not hidden: hidden would make the frame the scroll container its sticky row details pin to.
				data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_TABLE}
			>
				<TableToolbar
					searchPlaceholder={tCapManage('dealstack_ai.ingest.library.search_placeholder')}
					search={library.search}
					onSearchChange={library.onSearchChange}
					leadingCell={null}
					searchIcon={
						<IconSearch
							size={16}
							stroke={1.5}
							color="var(--mantine-color-gray-9)"
							style={{ flexShrink: 0 }}
						/>
					}
					lightDivider
					toolCells={
						<>
							<Menu {...DS_AI_MENU_PROPS} {...filterMenu}>
								<Menu.Target>
									<UnstyledButton
										type="button"
										styles={{
											root: dsAiToolbarButtonStyles({
												active: library.classification !== null,
											}),
										}}
										data-testid={
											TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_LIBRARY_CLASSIFICATION_FILTER
										}
									>
										<Icon variant="filter" size="xl" />
										{library.classification ??
											tCapManage('dealstack_ai.ingest.library.filter')}
									</UnstyledButton>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item onClick={() => library.onClassificationChange(null)}>
										{tCapManage(
											'dealstack_ai.ingest.library.all_classifications'
										)}
									</Menu.Item>
									<Menu.Divider />
									{library.classifications.map((classification) => (
										<Menu.Item
											key={classification}
											onClick={() =>
												library.onClassificationChange(classification)
											}
										>
											{classification}
										</Menu.Item>
									))}
								</Menu.Dropdown>
							</Menu>
							<UnstyledButton
								type="button"
								styles={{ root: dsAiToolbarButtonStyles({ square: true }) }}
								aria-label={tCapManage(
									isFullScreen
										? 'dealstack_ai.ingest.library.collapse'
										: 'dealstack_ai.ingest.library.expand'
								)}
								aria-pressed={isFullScreen}
								onClick={onToggleFullScreen}
							>
								<Icon
									variant={
										isFullScreen ? 'collapse-diagonally' : 'expand-diagonally'
									}
									size="xl"
								/>
							</UnstyledButton>
						</>
					}
				/>
				<LibraryHeaderRow>
					<DsAiCell $flex>
						<Group
							component="label"
							gap={12}
							miw={0}
							wrap="nowrap"
							styles={{ root: NAME_LABEL_STYLES }}
						>
							<Checkbox
								size="xs"
								radius="sm"
								aria-label={tCapManage(
									'dealstack_ai.ingest.library.select_all_on_page'
								)}
								checked={library.pageSelection === 'all'}
								indeterminate={library.pageSelection === 'some'}
								onChange={library.onTogglePage}
								disabled={!rows.some((row) => row.selectable)}
							/>
							{tCapManage('dealstack_ai.ingest.library.columns.name')}
						</Group>
					</DsAiCell>
					<DsAiCell>
						{tCapManage('dealstack_ai.ingest.library.columns.classification')}
					</DsAiCell>
					<DsAiCell>
						{tCapManage('dealstack_ai.ingest.library.columns.related_people')}
					</DsAiCell>
					<DsAiCell>
						{tCapManage('dealstack_ai.ingest.library.columns.last_updated')}
					</DsAiCell>
					<DsAiCell>{tCapManage('dealstack_ai.ingest.library.columns.pages')}</DsAiCell>
					<DsAiCell />
				</LibraryHeaderRow>
				{library.isLoading &&
					Array.from({ length: SKELETON_ROWS }, (_, index) => (
						<DsAiGridRow key={index} $template={LIBRARY_COLUMNS} $lightDivider>
							<DsAiCell>
								<Skeleton height={16} width="70%" />
							</DsAiCell>
							<DsAiCell>
								<Skeleton height={16} width="60%" />
							</DsAiCell>
							<DsAiCell>
								<Skeleton height={16} width="50%" />
							</DsAiCell>
							<DsAiCell>
								<Skeleton height={16} width="50%" />
							</DsAiCell>
							<DsAiCell>
								<Skeleton height={16} width="40%" />
							</DsAiCell>
							<DsAiCell />
						</DsAiGridRow>
					))}
				{library.isError && (
					<Box py="lg" px={12} ta="center" c="dark.0" fz="md" role="alert">
						{tCapManage('dealstack_ai.ingest.library.load_failed')}
					</Box>
				)}
				{!library.isLoading && !library.isError && rows.length === 0 && (
					<Box py="lg" px={12} ta="center" c="dark.0" fz="md">
						{tCapManage('dealstack_ai.ingest.library.empty')}
					</Box>
				)}
				{rows.map((row) => (
					<LibraryRow key={row.id} row={row} onToggle={library.onToggleRow} />
				))}
			</LibraryFrame>
			<TablePagination
				page={library.page}
				pageCount={library.pageCount}
				pageSize={library.pageSize}
				visibleRows={rows.length}
				pageSizeOptions={LIBRARY_PAGE_SIZE_OPTIONS}
				onPageChange={library.onPageChange}
				onPageSizeChange={library.onPageSizeChange}
				pageSizeMenu={pageSizeMenu}
			/>
		</Stack>
	);
};

/** The library table frame, clipping its rows to the rounded corner. */
const LibraryFrame = styled(Box)<StyledMantineProps>({ borderRadius: '4px', overflow: 'clip' });

/** The library table's header row. */
const LibraryHeaderRow = styled(Box)<StyledMantineProps>({
	...dsAiGridRowStyles({ template: LIBRARY_COLUMNS, lightDivider: true }),
	...DSAI_HEADER_ROW_STYLES,
});
